/**
 * Retry utilities with exponential backoff
 */

import { RetryOptions } from '../types';

/**
 * Default retry options
 */
export const DEFAULT_RETRY_OPTIONS: RetryOptions = {
  initialDelay: 1000, // 1 second
  maxDelay: 60000, // 60 seconds
  maxAttempts: Infinity,
  multiplier: 2,
  jitter: true,
};

/**
 * Calculate the delay for the next retry attempt using exponential backoff
 * 
 * @param attempt - Current attempt number (0-indexed)
 * @param options - Retry options
 * @returns Delay in milliseconds
 */
export function calculateDelay(attempt: number, options: Partial<RetryOptions> = {}): number {
  const opts = { ...DEFAULT_RETRY_OPTIONS, ...options };
  
  // Calculate exponential delay: initialDelay * multiplier^attempt
  let delay = opts.initialDelay * Math.pow(opts.multiplier || 2, attempt);
  
  // Cap at maxDelay
  delay = Math.min(delay, opts.maxDelay);
  
  // Add jitter to prevent thundering herd problem
  if (opts.jitter) {
    // Add random jitter between 0% and 25% of the delay
    const jitterAmount = delay * 0.25 * Math.random();
    delay = delay + jitterAmount;
  }
  
  return Math.floor(delay);
}

/**
 * Check if we should retry based on the current attempt number
 * 
 * @param attempt - Current attempt number (0-indexed)
 * @param options - Retry options
 * @returns Whether to retry
 */
export function shouldRetry(attempt: number, options: Partial<RetryOptions> = {}): boolean {
  const opts = { ...DEFAULT_RETRY_OPTIONS, ...options };
  return attempt < opts.maxAttempts;
}

/**
 * Sleep for a specified number of milliseconds
 * 
 * @param ms - Milliseconds to sleep
 * @returns Promise that resolves after the delay
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry a function with exponential backoff
 * 
 * @param fn - Function to retry
 * @param options - Retry options
 * @returns Promise that resolves with the function result or rejects with the last error
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: Partial<RetryOptions> = {}
): Promise<T> {
  const opts = { ...DEFAULT_RETRY_OPTIONS, ...options };
  let lastError: Error | undefined;
  
  for (let attempt = 0; attempt < opts.maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      
      // Check if we should retry
      if (attempt + 1 >= opts.maxAttempts) {
        break;
      }
      
      // Calculate delay and wait
      const delay = calculateDelay(attempt, opts);
      await sleep(delay);
    }
  }
  
  // If we get here, all retries failed
  throw lastError || new Error('Retry failed with unknown error');
}

/**
 * Create a retry strategy with pre-configured options
 * 
 * @param options - Retry options
 * @returns Function that retries with the given options
 */
export function createRetryStrategy(options: Partial<RetryOptions> = {}) {
  return <T>(fn: () => Promise<T>): Promise<T> => {
    return retryWithBackoff(fn, options);
  };
}

